import React from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {useDownloadTracker} from '../hooks/useAnalytics';

const HeroContent=()=> {
  const trackDownload=useDownloadTracker();

  const handleScroll=(e,targetId)=> {
    e.preventDefault();
    const element=document.getElementById(targetId);
    if (element) element.scrollIntoView({behavior: 'smooth'});
  };

  return (
    <motion.div initial={{opacity: 0,x: -50}} animate={{opacity: 1,x: 0}} transition={{duration: 0.8,delay: 0.5,ease: "easeOut"}} className="w-full py-8 lg:py-0">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-black rounded-full text-white text-xs sm:text-sm font-bold mb-6 shadow-lg lg:mt-20">
        <span className="relative flex h-2 w-2 sm:h-2.5 sm:w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-red opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 sm:h-2.5 sm:w-2.5 bg-brand-red"></span>
        </span>
        The #1 Reward-Based Fitness App
      </div>

      <h1 className="text-4xl sm:text-5xl lg:text-5xl xl:text-6xl font-black text-gray-900 leading-[1.1] mb-5 tracking-tight">
        Find a Fitness Buddy &{' '}
        <span className="text-brand-red">Earn Real Rewards</span>
      </h1>

      <p className="text-base sm:text-lg text-gray-600 mb-8 leading-relaxed max-w-md font-medium">
        FitPair connects you with workout partners nearby. Complete daily fitness challenges,earn tokens,and redeem real rewards from top brands.
      </p>

      <div className="flex flex-col gap-4 mb-8">
        <div className="flex flex-wrap gap-3">
          <motion.a href="https://apps.apple.com/sg/app/fitpair-find-a-buddy-nearby/id1619236607" target="_blank" rel="noopener noreferrer" onClick={()=> trackDownload('App Store','Hero')} whileHover={{scale: 1.05,y: -2}} whileTap={{scale: 0.95}} className="flex items-center gap-3 bg-black text-white px-5 py-2.5 rounded-xl font-bold transition-all shadow-lg hover:bg-gray-900" aria-label="Download FitPair on the Apple App Store">
            <svg viewBox="0 0 24 24" className="w-5 h-5 sm:w-6 sm:h-6 fill-current flex-shrink-0" aria-hidden="true">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
            </svg>
            <div className="text-left leading-tight">
              <p className="text-[9px] opacity-70 uppercase tracking-wider">Download on the</p>
              <p className="text-xs sm:text-sm font-black">App Store</p>
            </div>
          </motion.a>

          <motion.a href="https://play.google.com/store/apps/details?id=com.app.gympair&hl=en-SG" target="_blank" rel="noopener noreferrer" onClick={()=> trackDownload('Play Store','Hero')} whileHover={{scale: 1.05,y: -2}} whileTap={{scale: 0.95}} className="flex items-center gap-3 bg-black text-white px-5 py-2.5 rounded-xl font-bold transition-all shadow-lg hover:bg-gray-900" aria-label="Download FitPair on Google Play Store">
            <svg viewBox="0 0 24 24" className="w-5 h-5 sm:w-6 sm:h-6 fill-current flex-shrink-0" aria-hidden="true">
              <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
            </svg>
            <div className="text-left leading-tight">
              <p className="text-[9px] opacity-70 uppercase tracking-wider">Get it on</p>
              <p className="text-xs sm:text-sm font-black">Google Play</p>
            </div>
          </motion.a>
        </div>

        <a href="#brand-story" onClick={(e)=> handleScroll(e,'brand-story')}>
          <motion.button whileHover={{x: 5}} className="flex items-center gap-2 text-gray-900 font-black text-sm sm:text-base group w-fit">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-gray-200 flex items-center justify-center group-hover:border-brand-red group-hover:text-brand-red transition-colors">
              <SafeIcon icon={FiIcons.FiPlay} className="ml-0.5" />
            </div>
            <span>How it works</span>
          </motion.button>
        </a>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex -space-x-2.5">
          {[44,32,68,12].map((id,i)=> (
            <img key={i} src={`https://randomuser.me/api/portraits/${i % 2===0 ? 'women' : 'men'}/${id}.jpg`} alt={`FitPair user ${i + 1} profile photo`} className="w-8 h-8 sm:w-9 sm:h-9 rounded-full border-2 border-white object-cover" loading="lazy" width="36" height="36" />
          ))}
        </div>
        <div className="text-xs sm:text-sm font-bold text-gray-600">
          <div className="flex text-yellow-400 text-xs mb-0.5">
            {[...Array(5)].map((_,i)=> (
              <SafeIcon key={i} icon={FiIcons.FiStar} className="fill-current" />
            ))}
          </div>
          Joined by 10,000+ users globally
        </div>
      </div>
    </motion.div>
  );
};

export default HeroContent;